import type { StoreOverview } from "@/features/store-overview/storeOverviewSchemas";
import {
  formatShareBps,
  formatStoreOverviewAmount,
} from "@/features/store-overview/storeOverviewPresentation";

interface StoreOverviewTrendChartProps {
  series: StoreOverview["series"];
}

export function StoreOverviewTrendChart({
  series,
}: StoreOverviewTrendChartProps) {
  const visibleSeries = series.filter((item) => item.points.length > 0);
  const values = visibleSeries.flatMap((item) =>
    item.points.map((point) => BigInt(point.value)),
  );
  const maximum = values.reduce(
    (current, value) => (value > current ? value : current),
    1n,
  );
  const chartSeries = visibleSeries.map((item) => {
    const coordinates = item.points.map((point, index) => {
      const value = BigInt(point.value);
      return {
        x:
          item.points.length <= 1
            ? 50
            : (index / (item.points.length - 1)) * 100,
        y:
          48 -
          Number(((value > 0n ? value : 0n) * 4_000n) / maximum) /
            100,
      };
    });
    return {
      ...item,
      coordinates,
      label:
        item.key === "ad_spend_minor" ? "广告消耗" : "销售额",
      path: coordinates
        .map(
          (point, index) =>
            `${index === 0 ? "M" : "L"}${point.x},${point.y}`,
        )
        .join(" "),
    };
  });

  if (chartSeries.length === 0) {
    return <p className="store-overview-empty-copy">当前范围暂无趋势数据。</p>;
  }

  return (
    <div className="store-overview-chart">
      <ul aria-label="经营趋势图例" className="store-overview-chart__legend">
        {chartSeries.map((item) => (
          <li data-series={item.key} key={item.key}>
            <i aria-hidden="true" />
            {item.label}
          </li>
        ))}
      </ul>
      <svg
        aria-labelledby="store-overview-trend-title store-overview-trend-description"
        className="store-overview-chart__svg"
        role="img"
        viewBox="0 0 100 52"
      >
        <title id="store-overview-trend-title">全店经营趋势</title>
        <desc id="store-overview-trend-description">
          {`包含${chartSeries.map((item) => item.label).join("和")}，共 ${chartSeries.reduce((total, item) => total + item.points.length, 0)} 个服务端聚合时间点，详细数值见图表后的数据表。`}
        </desc>
        {[8, 18, 28, 38, 48].map((y) => (
          <line className="store-overview-chart__grid" key={y} x1="0" x2="100" y1={y} y2={y} />
        ))}
        {chartSeries.map((item, seriesIndex) => (
          <g data-series={item.key} key={item.key}>
            {seriesIndex === 0 ? (
              <path className="store-overview-chart__area" d={`${item.path} L100,50 L0,50 Z`} />
            ) : null}
            <path className="store-overview-chart__line" d={item.path} />
            {item.coordinates.map((point, index) => {
              const sourcePoint = item.points[index]!;
              const tooltip = `${item.label}，${new Date(sourcePoint.start).toLocaleString("zh-CN")}：${formatStoreOverviewAmount(sourcePoint.value, sourcePoint.currency)}`;
              return (
                <circle
                  aria-label={tooltip}
                  className="store-overview-chart__point"
                  cx={point.x}
                  cy={point.y}
                  key={sourcePoint.start}
                  r="1.6"
                  role="img"
                  tabIndex={0}
                >
                  <title>{tooltip}</title>
                </circle>
              );
            })}
          </g>
        ))}
      </svg>
      <table className="sr-only">
        <caption>全店经营趋势数据</caption>
        <thead><tr><th>指标</th><th>时间</th><th>金额</th></tr></thead>
        <tbody>
          {chartSeries.flatMap((item) =>
            item.points.map((point) => (
              <tr key={`${item.key}:${point.start}`}>
                <th scope="row">{item.label}</th>
                <td>{new Date(point.start).toLocaleString("zh-CN")}</td>
                <td>{formatStoreOverviewAmount(point.value, point.currency)}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </div>
  );
}

interface StoreOverviewCompositionChartProps {
  items: StoreOverview["composition"]["platform"];
  label: string;
}

export function StoreOverviewCompositionChart({
  items,
  label,
}: StoreOverviewCompositionChartProps) {
  const colors = ["#2079ff", "#68b5ff", "#ffb049", "#9b7bff", "#66c49a"];

  if (items.length === 0) {
    return <p className="store-overview-empty-copy">暂无{label}结构数据。</p>;
  }

  return (
    <div className="store-overview-composition">
      <svg
        aria-label={`${label}结构，详细数值见旁侧图例`}
        className="store-overview-composition__ring"
        role="img"
        viewBox="0 0 42 42"
      >
        <circle className="store-overview-composition__track" cx="21" cy="21" fill="none" r="15.9" strokeWidth="6" />
        {items.map((item, index) => {
          const share = Math.max(0, Math.min(100, Number(item.shareBps) / 100));
          const currentOffset = items
            .slice(0, index)
            .reduce(
              (total, previous) =>
                total +
                Math.max(0, Math.min(100, Number(previous.shareBps) / 100)),
              0,
            );
          return (
            <circle
              className="store-overview-composition__segment"
              cx="21"
              cy="21"
              fill="none"
              key={item.key}
              r="15.9"
              stroke={colors[index % colors.length]}
              strokeDasharray={`${share} ${100 - share}`}
              strokeDashoffset={-currentOffset}
              strokeWidth="6"
            />
          );
        })}
      </svg>
      <ul aria-label={`${label}结构明细`} className="store-overview-composition__legend">
        {items.map((item, index) => (
          <li key={item.key}>
            <i aria-hidden="true" style={{ background: colors[index % colors.length] }} />
            <span>{item.label}</span>
            <strong>{formatShareBps(item.shareBps)}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}
